import { useCallback, useMemo, useState, useEffect, useRef } from 'react';

import {
  TYPE_BRACKET_OPEN,
  TYPE_BRACKET_CLOSE,
  TYPE_OPERATOR,
  TYPE_METRIC,
  TYPE_COHORT_METRIC,
  TYPE_DECIMAL,
} from './constants';
import {
  parseFormula,
  stringifyFormulaHint,
  stringifyFormula,
  countOpenBrackets,
} from './utils';

export const useCustomMetricCalculator = ({
  value = {},
  onChange,
  metrics,
  cohortMetrics,
}) => {
  const [tokens, setTokens] = useState(() => parseFormula(value.formula));
  const formulaRef = useRef(value.formula);

  const executeChange = useCallback(
    (t) => {
      const formula = stringifyFormula(t);
      if (formulaRef.current !== formula) {
        const displayFormula = stringifyFormulaHint(t, metrics, cohortMetrics);
        formulaRef.current = formula;
        onChange(
          {},
          {
            formula,
            displayFormula,
          }
        );
      }
    },
    [onChange, metrics, cohortMetrics]
  );

  const addToken = useCallback((token) => setTokens((t) => [...t, token]), []);

  const editToken = useCallback((index, value) => {
    setTokens((t) => {
      return t.map((token, i) => {
        if (i === index) {
          return {
            ...token,
            value,
          };
        }
        return token;
      });
    });
  }, []);

  const removeToken = useCallback((index) => {
    setTokens((t) => [...t.slice(0, index), ...t.slice(index + 1)]);
  }, []);

  const types = useMemo(() => {
    const [last] = tokens.slice(-1);
    const openBracketCount = countOpenBrackets(tokens);

    if (!last) {
      return [TYPE_BRACKET_OPEN, TYPE_METRIC, TYPE_COHORT_METRIC, TYPE_DECIMAL];
    } else if (
      [TYPE_METRIC, TYPE_COHORT_METRIC, TYPE_DECIMAL].includes(last.type)
    ) {
      if (!openBracketCount) {
        return [TYPE_OPERATOR];
      }
      return [TYPE_OPERATOR, TYPE_BRACKET_CLOSE];
    } else if (TYPE_BRACKET_OPEN === last.type) {
      return [TYPE_METRIC, TYPE_COHORT_METRIC, TYPE_DECIMAL, TYPE_BRACKET_OPEN];
    } else if (TYPE_BRACKET_CLOSE === last.type) {
      if (openBracketCount) {
        return [TYPE_OPERATOR, TYPE_BRACKET_CLOSE];
      }
      return [TYPE_OPERATOR];
    } else if (TYPE_OPERATOR === last.type) {
      return [TYPE_DECIMAL, TYPE_METRIC, TYPE_COHORT_METRIC, TYPE_BRACKET_OPEN];
    }
  }, [tokens]);

  useEffect(() => {
    executeChange(tokens);
  }, [tokens]);

  useEffect(() => {
    const { formula = '' } = value;

    if (formula !== formulaRef.current) {
      setTokens(parseFormula(formula));
    }
  }, [value]);

  return {
    addToken,
    editToken,
    removeToken,
    types,
    tokens,
  };
};
